import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import UnderlineTabs from "@/components/UnderlineTabs";
import SelectDropDown from "@/components/Common/SelectDropDown";
import { PICKER_STYLES } from "@/helpers/pickerStyles";
import { useDetectShowcaseSite, useSaveShowcaseSettings, useWebsitePreview } from "@/hooks/useShowcase";
import { HEADLINE_MAX, headlineErrorOf } from "@/components/ShowcaseManager/showcaseManagerModel";
import { LAYOUTS, LOOKS, themeLineOf } from "./showcaseSetupModel";

const DEVICES = [
  { key: "desktop", label: "Desktop" },
  { key: "phone", label: "Phone" },
];

const optionsOf = (items) => items.map((item) => ({ value: item.key, label: item.label }));
const LAYOUT_OPTIONS = optionsOf(LAYOUTS);

export const siteStyleOf = (look, theme) => {
  if (look !== "match" || !theme) return {};
  return {
    fontFamily: theme.bodyFont ? `${theme.bodyFont}, sans-serif` : undefined,
    color: theme.text || undefined,
    background: theme.background || undefined,
  };
};

const ShadowHtml = ({ html }) => {
  const hostRef = useRef(null);
  const shadowRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    if (!shadowRef.current) {
      shadowRef.current = host.attachShadow ? host.attachShadow({ mode: "open" }) : host;
      shadowRef.current.addEventListener("click", (event) => {
        if (event.target?.closest?.("a, button")) event.preventDefault();
      });
    }
    shadowRef.current.innerHTML = html || "";
  }, [html]);

  return <div ref={hostRef} className="website-preview__cards" />;
};

const LookControls = ({ brokerageId, showcase, look, onDraft }) => {
  const save = useSaveShowcaseSettings(brokerageId);
  const detect = useDetectShowcaseSite(brokerageId);
  const firstSite = showcase.sites?.[0] || null;
  const [headline, setHeadline] = useState(showcase.headline || "");
  const [headlineError, setHeadlineError] = useState("");
  const read = themeLineOf(showcase.theme);
  const lookOptions = LOOKS.map((item) => ({
    value: item.key,
    label: item.key === "match" && read ? `Match my site (${read})` : item.key === "match" ? "Match my site" : item.label,
  }));
  const current = look.look || "match";

  useEffect(() => {
    setHeadline(showcase.headline || "");
  }, [brokerageId, showcase.headline]);

  const fail = (error) => toast.error(error.message);

  const choose = (field) => (option) => {
    if (!option) return;
    onDraft({ [field]: option.value });
    save.mutate({ [field]: option.value }, { onError: fail });
  };

  const saveHeadline = () =>
    save.mutate(
      { headline: headline.trim() || null },
      {
        onSuccess: () => onDraft({ headline: headline.trim() || null }),
        onError: (error) => {
          const field = error.fields?.headline;
          if (field) setHeadlineError(headlineErrorOf(field, error.fields?.headlineTerm));
          else fail(error);
        },
      },
    );

  return (
    <div className="website-preview__controls">
      <div className="website-preview__pickers">
        <div className="showcase-setup__field">
          <label htmlFor="showcase-look">Look</label>
          <SelectDropDown
            id="showcase-look"
            classNamePrefix="select"
            options={lookOptions}
            value={lookOptions.find((option) => option.value === current) || lookOptions[0]}
            styles={PICKER_STYLES}
            errorClass="d-none"
            onChange={choose("look")}
          />
        </div>
        <div className="showcase-setup__field">
          <label htmlFor="showcase-layout">Layout</label>
          <SelectDropDown
            id="showcase-layout"
            classNamePrefix="select"
            options={LAYOUT_OPTIONS}
            value={LAYOUT_OPTIONS.find((option) => option.value === look.layout) || LAYOUT_OPTIONS[0]}
            styles={PICKER_STYLES}
            errorClass="d-none"
            onChange={choose("layout")}
          />
        </div>
      </div>
      {firstSite && current === "match" ? (
        <button
          type="button"
          className="showcase-setup__quiet website-preview__reread"
          disabled={detect.isPending}
          onClick={() => detect.mutate(firstSite.id, { onError: fail })}
        >
          {detect.isPending ? `Reading ${firstSite.host}…` : `Read ${firstSite.host} again`}
        </button>
      ) : null}
      <div className="showcase-setup__field">
        <label htmlFor="showcase-headline">Section title</label>
        <div className="showcase-setup__row">
          <input
            id="showcase-headline"
            value={headline}
            maxLength={HEADLINE_MAX}
            onChange={(event) => {
              setHeadline(event.target.value);
              setHeadlineError("");
              onDraft({ headline: event.target.value.trim() || null });
            }}
            placeholder={showcase.defaultHeadline || "Buyers we are working with"}
            aria-invalid={Boolean(headlineError)}
          />
          <button
            type="button"
            className="showcase-setup__link"
            disabled={(headline.trim() || null) === (showcase.headline || null) || save.isPending}
            onClick={saveHeadline}
          >
            Save
          </button>
        </div>
        {headlineError ? (
          <p className="showcase-setup__error" role="alert">
            {headlineError}
          </p>
        ) : null}
      </div>
    </div>
  );
};

const WebsitePreview = ({ brokerageId, showcase, look, onDraft }) => {
  const [device, setDevice] = useState("desktop");
  const [debounced, setDebounced] = useState(look);
  const current = look.look || "match";

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(look), 350);
    return () => clearTimeout(timer);
  }, [look.look, look.layout, look.headline]);

  const preview = useWebsitePreview(brokerageId, debounced);

  return (
    <aside className="website-preview" aria-label="Preview of the cards on your website">
      <LookControls brokerageId={brokerageId} showcase={showcase} look={look} onDraft={onDraft} />
      <div className="website-preview__bar">
        <span className="website-preview__label">Preview</span>
        <UnderlineTabs tabs={DEVICES} value={device} onChange={setDevice} label="Preview size" />
      </div>
      <div className={`website-preview__stage is-${device}`}>
        <div className="website-preview__site" style={siteStyleOf(current, showcase.theme)}>
          {preview.isError ? (
            <p className="website-preview__note">The preview did not load. Your settings still&nbsp;saved.</p>
          ) : preview.data === undefined ? (
            <p className="website-preview__note" aria-busy="true">
              Loading the preview…
            </p>
          ) : preview.data.live ? (
            <ShadowHtml html={preview.data.html} />
          ) : (
            <p className="website-preview__note">Your Buyer Want&#8209;Listings show here once you&nbsp;subscribe.</p>
          )}
        </div>
      </div>
      <p className="website-preview__note website-preview__note--foot">
        {current === "match"
          ? "These are your agents' real public Buyer Want‑Listings. On your site the cards take its fonts and colors."
          : "These are your agents' real public Buyer Want‑Listings, exactly as your site will show them."}
      </p>
    </aside>
  );
};

export default WebsitePreview;
